import { select, call, takeLatest } from 'redux-saga/effects';

import { CaseNoPrincipalActions, OrderExpiredReportActions } from '../constants';
import { CaseNoPrincipalApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteCaseNoPrincipalAction,
    GetCaseNoPrincipalAction,
    GetCaseNoPrincipalsAction,
} from '../actions/caseNoPrincipal-actions';

import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure } from '../../services/api-handlers/api-resolver';
import { groupCaseNoPrincipalsToCsv } from '../../helpers/groupCaseNoPrincipalsToCsv';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';

function* handleFetchCaseNoPrincipals(action: GetCaseNoPrincipalsAction) {
    const oldData = yield select((state: StoreState) => state.casesNoPrincipal.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        CaseNoPrincipalApi.getCaseNoPrincipals({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchCaseNoPrincipalsCsv(action: GetCaseNoPrincipalsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            CaseNoPrincipalApi.getCaseNoPrincipals({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetCaseNoPrincipalsAction = {
                    type: OrderExpiredReportActions.FETCH_ORDER_EXPIRED_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchCaseNoPrincipalsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupCaseNoPrincipalsToCsv(dataAll);
                    exportToCsv('case-no-principals.csv', groupCsv);
                } else {
                    const groupCsvData = groupCaseNoPrincipalsToCsv(data.payload);
                    exportToCsv('case-no-principals.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchCaseNoPrincipal(action: GetCaseNoPrincipalAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => CaseNoPrincipalApi.getCaseNoPrincipal(id));
}

function* handleDeleteCaseNoPrincipal(action: DeleteCaseNoPrincipalAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => CaseNoPrincipalApi.deleteCaseNoPrincipal(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(CaseNoPrincipalActions.FETCH_CASE_NO_PRINCIPALS, handleFetchCaseNoPrincipals);
    yield takeLatest(CaseNoPrincipalActions.FETCH_CASE_NO_PRINCIPALS_CSV, handleFetchCaseNoPrincipalsCsv);
    yield takeLatest(CaseNoPrincipalActions.FETCH_CASE_NO_PRINCIPAL, handleFetchCaseNoPrincipal);
    yield takeLatest(CaseNoPrincipalActions.DELETE_CASE_NO_PRINCIPAL, handleDeleteCaseNoPrincipal);
}
